import React, { createContext, useState, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { logger } from './logging'

interface ErrorContextProps {
  error: string | null
  setError: (errorMessage: any) => void
}

const ErrorContext = createContext<ErrorContextProps>({
  error: null,
  setError: () => { }
})

export const useErrorContext = () => useContext(ErrorContext)

function isAuthError(error: any): boolean {
  if (!error) return false

  // Axios error with 401 status
  if (error.response?.status === 401) return true

  // Note: ERR_NETWORK is NOT treated as an auth error. While CORS-blocked 401
  // responses from API Gateway produce ERR_NETWORK, so do connection refused
  // errors when the backend is simply unreachable. We cannot distinguish between
  // the two, and treating all network errors as auth failures causes infinite
  // redirect loops when the server is down.

  // Cognito-specific error messages
  const message = (error.message || error || '').toString().toLowerCase()
  if (message.includes('not authenticated') ||
      message.includes('token has expired') ||
      message.includes('invalid token') ||
      message.includes('no current user') ||
      message.includes('client not authenticated')) {
    return true
  }

  return false
}

export const ErrorProvider = ({ children }: { children: React.ReactNode }) => {
  const [error, setErrorState] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()

  const setError = (errorMessage: any) => {
    if (!errorMessage) {
      setErrorState(null)
      return
    }

    if (isAuthError(errorMessage)) {
      logger.warn('Session expired, redirecting to login')
      setErrorState(null)
      navigate('/login', { state: { from: location }, replace: true })
      return
    }

    setErrorState(errorMessage)
  }

  return (
        <ErrorContext.Provider value={{ error, setError }}>
            {children}
        </ErrorContext.Provider>
  )
}
